import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { TrendingUp, Mail, Lock, User, Loader2, Eye, EyeOff, Gift, Shield, ArrowLeft } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function AuthPage() {
  const { login, register, loginWithGoogle } = useAuth();
  const { t, language } = useLanguage();
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot" | "2fa"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [totp, setTotp] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [refCampaign, setRefCampaign] = useState("");

  useEffect(() => {
    try {
      const rc = localStorage.getItem("trp_ref_campaign");
      const ts = parseInt(localStorage.getItem("trp_ref_campaign_ts") || "0", 10);
      if (rc && ts && Date.now() - ts < 30 * 24 * 3600 * 1000) {
        setRefCampaign(rc);
        setMode("register");
      }
    } catch { /* noop */ }
    // Handle magic-link error redirects from /api/auth/magic/verify.
    try {
      const params = new URLSearchParams(window.location.search);
      const magic = params.get("magic");
      if (magic === "expired") {
        setMode("magic");
        setInfo(t.auth_magic_expired || "That login link expired or was already used. Enter your email and we'll send a new one.");
      } else if (magic === "sent") {
        setMode("magic");
        setInfo(t.auth_magic_sent || "Check your inbox — we just sent you a one-click login link.");
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMagicLink = async (e) => {
    e?.preventDefault?.();
    resetAlerts();
    if (!email || !email.includes("@")) {
      setError(t.auth_email_required || "Enter a valid email.");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/magic/request`, {
        email, language: language || "en", redirect_to: "/dashboard",
      });
      setInfo(t.auth_magic_sent || "Check your inbox — we just sent you a one-click login link.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetAlerts = () => { setError(""); setInfo(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetAlerts();
    setIsLoading(true);
    try {
      if (mode === "login") {
        const res = await login(email, password);
        if (res?.requires_2fa) { setMode("2fa"); setIsLoading(false); return; }
        window.location.href = "/dashboard";
      } else if (mode === "register") {
        await register(email, password, name);
        window.location.href = "/dashboard";
      } else if (mode === "2fa") {
        await login(email, password, totp);
        window.location.href = "/dashboard";
      } else if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email, language: language || "en" }, { withCredentials: true });
        setInfo(t.auth_forgot_sent || "If this email is registered, a reset link has been sent. Check your inbox.");
      } else if (mode === "magic") {
        await sendMagicLink(e);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const titleFor = {
    login: t.auth_welcome || "Welcome back",
    register: t.auth_create || "Create account",
    forgot: t.auth_forgot_title || "Reset your password",
    "2fa": t.auth_2fa_title || "Two-factor authentication",
    magic: t.auth_magic_title || "One-click login",
  }[mode];
  const subtitleFor = {
    login: t.auth_sign_in_desc || "Sign in to your TopRankPulse account",
    register: t.auth_create_desc || "Start monitoring your AI visibility",
    forgot: t.auth_forgot_desc || "Enter your email — we'll send you a reset link.",
    "2fa": t.auth_2fa_desc || "Enter the 6-digit code from your authenticator app (or a backup code).",
    magic: t.auth_magic_desc || "Enter your email — we'll send you a one-click login link. No password needed.",
  }[mode];

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-black font-['Chivo'] text-white">TopRankPulse</span>
          </a>
        </div>

        <div className="bg-gray-900 border border-gray-800 p-8">
          {(mode === "forgot" || mode === "2fa" || mode === "magic") && (
            <button onClick={() => { resetAlerts(); setMode("login"); setTotp(""); }} data-testid="auth-back"
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white mb-4">
              <ArrowLeft className="w-3.5 h-3.5" /> {t.auth_back || "Back to sign in"}
            </button>
          )}
          <h2 className="text-2xl font-bold text-white mb-1 font-['Chivo']" data-testid="auth-title">{titleFor}</h2>
          <p className="text-gray-400 text-sm mb-6">{subtitleFor}</p>

          {refCampaign && mode === "register" && (
            <div className="flex items-center gap-2 p-3 mb-6 rounded-lg bg-gradient-to-r from-pink-500/10 to-violet-500/10 border border-pink-500/30" data-testid="auth-referral-banner">
              <Gift className="w-5 h-5 text-pink-400 flex-shrink-0" />
              <div className="text-sm text-white">
                {t.auth_referral_banner || "You were invited via a campaign — sign up to claim 3 free ad credits."}
              </div>
            </div>
          )}

          {(mode === "login" || mode === "register") && (
            <>
              <button onClick={loginWithGoogle} data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-gray-900 font-semibold hover:bg-gray-100 transition-colors mb-6">
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {t.auth_google || "Continue with Google"}
              </button>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 h-px bg-gray-800" />
                <span className="text-gray-500 text-xs">{t.auth_or || "or"}</span>
                <div className="flex-1 h-px bg-gray-800" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t.auth_name || "Name"}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input data-testid="name-input" type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder={t.auth_name_ph || "Your name"}
                    className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none" />
                </div>
              </div>
            )}

            {mode !== "2fa" && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t.auth_email || "Email"}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.auth_email_ph || "you@company.com"} required
                    className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none" />
                </div>
              </div>
            )}

            {(mode === "login" || mode === "register") && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm text-gray-400">{t.auth_password || "Password"}</label>
                  {mode === "login" && (
                    <button type="button" onClick={() => { resetAlerts(); setMode("forgot"); }} data-testid="auth-forgot-link"
                      className="text-xs text-blue-400 hover:text-blue-300 font-medium">
                      {t.auth_forgot_link || "Forgot password?"}
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input data-testid="auth-password-input" type={showPass ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === "login" ? (t.auth_pwd_login_ph || "Your password") : (t.auth_pwd_register_ph || "Min 6 characters")}
                    required minLength={6}
                    className="w-full pl-10 pr-12 py-3 bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none" />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            {mode === "2fa" && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">{t.auth_2fa_code || "6-digit code or backup code"}</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input data-testid="auth-totp-input" type="text" value={totp} onChange={(e) => setTotp(e.target.value.trim())}
                    placeholder="123456" required autoFocus inputMode="numeric"
                    className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 text-white focus:border-blue-500 focus:outline-none tracking-widest font-mono" />
                </div>
              </div>
            )}

            {error && <div className="text-red-400 text-sm bg-red-900/20 border border-red-800 px-3 py-2" data-testid="auth-error">{error}</div>}
            {info && <div className="text-green-400 text-sm bg-green-900/20 border border-green-800 px-3 py-2" data-testid="auth-info">{info}</div>}

            <button data-testid="auth-submit-btn" type="submit" disabled={isLoading}
              className="w-full py-3 bg-blue-600 text-white font-bold hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {mode === "login" && (t.auth_sign_in || "Sign In")}
              {mode === "register" && (t.auth_create_btn || "Create Account")}
              {mode === "forgot" && (t.auth_forgot_cta || "Send reset link")}
              {mode === "2fa" && (t.auth_2fa_verify || "Verify & sign in")}
              {mode === "magic" && (t.auth_magic_cta || "Send me a login link")}
            </button>
          </form>

          {mode === "login" && (
            <button
              type="button"
              onClick={() => { resetAlerts(); setMode("magic"); }}
              data-testid="auth-magic-link-btn"
              className="w-full mt-3 py-2.5 bg-transparent border border-gray-700 text-gray-300 hover:bg-gray-800 hover:border-gray-600 text-sm font-bold uppercase tracking-wider transition-colors"
            >
              {t.auth_magic_cta_alt || "Email me a one-click login link"}
            </button>
          )}

          {(mode === "login" || mode === "register") && (
            <p className="text-center text-gray-500 text-sm mt-6">
              {mode === "login" ? (t.auth_no_account || "Don't have an account?") : (t.auth_have_account || "Already have an account?")}{" "}
              <button data-testid="auth-toggle" onClick={() => { resetAlerts(); setMode(mode === "login" ? "register" : "login"); }}
                className="text-blue-400 hover:text-blue-300 font-medium">
                {mode === "login" ? (t.auth_signup || "Sign up") : (t.auth_signin || "Sign in")}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
